// src/pages/Challenges/Challanges.tsx

import React, { useEffect, useState } from 'react';
import {
  useNavigate,
  useParams,
  useNavigationType,
  useLocation,
} from 'react-router-dom';
import './challengesstyle.css';
import '../../styles/modernDashboard.css';
import {
  assets_base_url,
  getChallengesForMap,
  getUserEvidences,
} from '../../globalService/api.service';
import BadgesRow from '../../components/BadgesRow';

import IconDistance from '../../assets/Distanceiconblack.png';
import IconTrainer from '../../assets/trainericon.png';
import IconCalendar from '../../assets/calendaricon.png';
import IconStart from '../../assets/start-icon.png';
import { computeActivityStats, type ActivityStats } from '../../utils/activityStats';

interface PersonalGoal {
  kmGoal: number;
  days: number;
  startDate: string;
  initialDistance: number;
}

export default function ChallengeComponent() {
  const navigate = useNavigate();
  const { brandId, userId } = useParams<{ brandId: string; userId: string }>();
  const navType = useNavigationType();
  const location = useLocation();

  const [challenges, setChallenges] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [activeTab, setActiveTab] = useState<1 | 2>(1);

  const totalActiveChallenges = challenges.filter(
    (c) =>
      c.percentage < 100 &&
      (c.daysLeftToComplete === undefined || (c.daysLeftToComplete || 0) > 0)
  ).length;
  const totalCompletedChallenges = challenges.filter(
    (c) => c.percentage >= 100 || (c.daysLeftToComplete || 0) <= 0
  ).length;

  const [filterPercentageSort, setFilterPercentageSort] = useState(false);
  const [filterTimeSort, setFilterTimeSort] = useState(false);
  const [filterZeroCompletion, setFilterZeroCompletion] = useState(false);
  const [filterDistanceGT0, setFilterDistanceGT0] = useState(false);
  const [isKilometers, setIsKilometers] = useState(true);

  const [totalTarget, setTotalTarget] = useState(0);
  const [totalSoFar, setTotalSoFar] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);

  const [activityStats, setActivityStats] = useState<ActivityStats>({
    currentStreak: 0,
    longestStreak: 0,
    activeDaysLast30: 0,
    activeDaysThisMonth: 0,
    uniqueDays: 0,
    lastActivityDay: undefined,
  });
  const [isActivityLoading, setIsActivityLoading] = useState(false);

  const motivatingPhrases = [
    'Keep pushing—every kilometer counts!',
    'You’re closer than you think. Let’s go!',
    'Small steps today, big gains tomorrow.',
    'Challenge yourself. Surpass yourself.',
    'The only bad workout is the one you didn’t do.',
    'Stay strong. Stay consistent.',
  ];
  const [motivationalMessage, setMotivationalMessage] = useState<string>('');


  const [personalGoal, setPersonalGoal] = useState<PersonalGoal | null>(null);
  const [kmGoalInput, setKmGoalInput] = useState<string>('');
  const [daysInput, setDaysInput] = useState<string>('');

  const [daysRemaining, setDaysRemaining] = useState<number>(0);
  const [kmRemaining, setKmRemaining] = useState<number>(0);

  useEffect(() => {
    if (navType === 'POP') {
      const saved = sessionStorage.getItem(`scrollPos-${location.key}`);
      if (saved) {
        window.scrollTo(0, parseInt(saved, 10));
      }
    } else {
      window.scrollTo(0, 0);
    }

    const idx = Math.floor(Math.random() * motivatingPhrases.length);
    setMotivationalMessage(motivatingPhrases[idx]);

    const stored = localStorage.getItem('personalGoal');
    if (stored) {
      try {
        const pg: PersonalGoal = JSON.parse(stored);
        setPersonalGoal(pg);
      } catch {
        localStorage.removeItem('personalGoal');
      }
    }

    localStorage.setItem('brandId', brandId || '');
    fetchChallenges();

    return () => {
      sessionStorage.setItem(
        `scrollPos-${location.key}`,
        window.scrollY.toString()
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchChallenges = async () => {
    setIsLoading(true);
    try {
      const data = await getChallengesForMap(userId || '');
      setChallenges(data || []);
      setFiltered(data || []);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Build activity stats from evidence across all challenges.
    if (!userId) return;
    if (!challenges || challenges.length === 0) {
      setActivityStats({
        currentStreak: 0,
        longestStreak: 0,
        activeDaysLast30: 0,
        activeDaysThisMonth: 0,
        uniqueDays: 0,
        lastActivityDay: undefined,
      });
      return;
    }

    const cacheKey = `activityStats_${userId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setActivityStats(JSON.parse(cached));
      } catch {
        // ignore
      }
    }

    (async () => {
      setIsActivityLoading(true);
      try {
        const productIds = Array.from(
          new Set((challenges || []).map((c) => c.productId).filter(Boolean))
        ) as string[];

        const evidenceLists = await Promise.all(
          productIds.map((pid) => getUserEvidences(pid, userId))
        );
        const all = ([] as any[]).concat(...(evidenceLists || []).filter(Boolean));
        const stats = computeActivityStats(all);
        setActivityStats(stats);
        sessionStorage.setItem(cacheKey, JSON.stringify(stats));
      } finally {
        setIsActivityLoading(false);
      }
    })();
  }, [challenges, userId]);

  useEffect(() => {
    if (!personalGoal) return;

    const start = new Date(personalGoal.startDate + 'T00:00:00');
    const deadline = new Date(
      start.getTime() + personalGoal.days * 24 * 60 * 60 * 1000
    );
    const now = new Date();
    const diffMs = deadline.getTime() - now.getTime();
    const remainingDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    setDaysRemaining(remainingDays > 0 ? remainingDays : 0);

    const currentTotalDistance = challenges.reduce(
      (acc, c) => acc + (c.distanceKm || 0),
      0
    );
    const usedKm = currentTotalDistance - (personalGoal.initialDistance || 0);
    const remKm = personalGoal.kmGoal - (usedKm > 0 ? usedKm : 0);
    setKmRemaining(remKm > 0 ? remKm : 0);
  }, [challenges, personalGoal]);

  useEffect(() => {
    let sumT = 0;
    let sumS = 0;

    challenges.forEach((c) => {
      sumT += c.totalDistanceKm || 0;
      sumS += c.distanceKm || 0;
    });

    setTotalTarget(sumT);
    setTotalSoFar(sumS);
  }, [challenges]);

  useEffect(() => {
    let baseArr: any[] = [];
    if (activeTab === 1) {
      baseArr = challenges.filter(
        (c) =>
          c.percentage < 100 &&
          (c.daysLeftToComplete === undefined ||
            (c.daysLeftToComplete || 0) > 0)
      );
    } else {
      baseArr = challenges.filter(
        (c) => c.percentage >= 100 || (c.daysLeftToComplete || 0) <= 0
      );
    }

    let arr = JSON.parse(JSON.stringify(baseArr)) as any[];

    if (filterZeroCompletion) {
      arr = arr.filter((c) => c.percentage === 0);
    }

    if (filterDistanceGT0) {
      arr = arr.filter((c) => c.distanceKm > 0);
    }

    if (filterPercentageSort) {
      arr.sort((a, b) => b.percentage - a.percentage);
    }

    if (filterTimeSort && activeTab === 1) {
      arr.sort((a, b) => {
        const aDays = a.daysLeftToComplete || 0;
        const bDays = b.daysLeftToComplete || 0;
        return aDays - bDays;
      });
    }

    setFiltered(arr);

    let comp = 0,
      act = 0;
    arr.forEach((c) => {
      if (c.percentage >= 100 || (c.daysLeftToComplete || 0) <= 0) comp++;
      else act++;
    });
    setCompletedCount(comp);
    setActiveCount(act);
  }, [
    challenges,
    activeTab,
    filterPercentageSort,
    filterTimeSort,
    filterZeroCompletion,
    filterDistanceGT0,
  ]);

  const handleTabChange = (tab: 1 | 2) => {
    if (tab === activeTab) return;
    setActiveTab(tab);

    setFilterPercentageSort(false);
    setFilterTimeSort(false);
    setFilterZeroCompletion(false);
    setFilterDistanceGT0(false);

    window.scrollTo(0, 0);
  };

  const handleGoalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const km = parseFloat(kmGoalInput);
    const days = parseInt(daysInput, 10);
    if (isNaN(km) || km <= 0 || isNaN(days) || days <= 0) return;

    const currentTotal = challenges.reduce(
      (acc, c) => acc + (c.distanceKm || 0),
      0
    );
    const today = new Date().toISOString().split('T')[0];
    const pg: PersonalGoal = {
      kmGoal: km,
      days,
      startDate: today,
      initialDistance: currentTotal,
    };
    setPersonalGoal(pg);
    localStorage.setItem('personalGoal', JSON.stringify(pg));
    setKmGoalInput('');
    setDaysInput('');
  };

  const getCardAccentClass = (pct: number) => {
    if (pct <= 1) return 'accent-white';
    if (pct <= 20) return 'accent-light-bronze';
    if (pct <= 40) return 'accent-bronze';
    if (pct <= 60) return 'accent-light-silver';
    if (pct <= 75) return 'accent-silver';
    if (pct <= 90) return 'accent-light-gold';
    return 'accent-gold';
  };

  const getMapImage = (img: string) => {
    try {
      const parsed = JSON.parse(img);
      return `${assets_base_url}/${parsed.image_intro}`;
    } catch {
      return img;
    }
  };

  return (
    <div className="vc-page">
      <div className="vc-container">
        <div className="vc-headerRow">
          <div>
            <div className="vc-title">Challenges</div>
            <div className="vc-subtitle">{motivationalMessage}</div>
          </div>
          <div className="vc-row" style={{ gap: 10 }}>
            <button
              className="vc-btnGhost"
              onClick={() => setIsKilometers((v) => !v)}
              title="Toggle units"
            >
              {isKilometers ? 'KM' : 'Mi'}
            </button>
          </div>
        </div>

        <div className="vc-grid">
          {/* Today's Goal */}
          <section className="vc-card vc-cardPad">
            <div className="vc-cardTitle">
              <span>Today's Goal</span>
              <span className="vc-pill">Mini goal</span>
            </div>

        {!personalGoal ? (
          <form onSubmit={handleGoalSubmit} className="goal-form-refined">
            <label className="inline-label">
              KM Goal:
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={kmGoalInput}
                onChange={(e) => setKmGoalInput(e.target.value)}
                placeholder="e.g. 50"
                className="goal-input-large"
              />
            </label>
            <label className="inline-label">
              Days:
              <input
                type="number"
                min="1"
                step="1"
                value={daysInput}
                onChange={(e) => setDaysInput(e.target.value)}
                placeholder="e.g. 14"
                className="goal-input-large"
              />
            </label>
            <button type="submit" className="goal-button-small">
              Set
            </button>
          </form>
        ) : (
          <div className="goal-countdown-container">
            {kmRemaining <= 0 && daysRemaining > 0 ? (
              <div className="goal-success">
                🎉 Well done! Personal Target Complete. 🎉
                <button
                  className="button-clear-goal-small"
                  onClick={() => {
                    localStorage.removeItem('personalGoal');
                    setPersonalGoal(null);
                  }}
                >
                  Reset
                </button>
              </div>
            ) : daysRemaining <= 0 && kmRemaining > 0 ? (
              <div className="goal-failed">
                ⏰ Never mind—time’s up. Reset your goal and let’s go again!
                <button
                  className="button-clear-goal-small"
                  onClick={() => {
                    localStorage.removeItem('personalGoal');
                    setPersonalGoal(null);
                  }}
                >
                  Reset
                </button>
              </div>
            ) : (
              <div className="countdown-row">
                <div className="countdown-box">
                  <div className="box-label">Days Left</div>
                  <div className="box-value">{daysRemaining}</div>
                </div>
                <div className="countdown-box">
                  <div className="box-label">KM Left</div>
                  <div className="box-value">{kmRemaining.toFixed(1)}</div>
                </div>
                <button
                  className="button-clear-goal-small-inline"
                  onClick={() => {
                    localStorage.removeItem('personalGoal');
                    setPersonalGoal(null);
                  }}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}
          </section>

          {/* Activity */}
          <section className="vc-card vc-cardPad">
            <div className="vc-cardTitle">
              <span>Activity</span>
              <span className="vc-muted" style={{ fontWeight: 700, fontSize: 12 }}>
                {activityStats.lastActivityDay ? `Last: ${activityStats.lastActivityDay}` : 'No activity yet'}
              </span>
            </div>

            <div className="vc-kpiRow">
              <div className="vc-kpi">
                <div className="vc-kpiLabel">🔥 Current streak</div>
                <div className="vc-kpiValue">
                  {isActivityLoading ? '…' : activityStats.currentStreak}
                </div>
                <div className="vc-kpiHint">days in a row</div>
              </div>
              <div className="vc-kpi">
                <div className="vc-kpiLabel">🏆 Best streak</div>
                <div className="vc-kpiValue">
                  {isActivityLoading ? '…' : activityStats.longestStreak}
                </div>
                <div className="vc-kpiHint">all-time</div>
              </div>
              <div className="vc-kpi">
                <div className="vc-kpiLabel">📅 Active days</div>
                <div className="vc-kpiValue">
                  {isActivityLoading ? '…' : activityStats.activeDaysLast30}
                </div>
                <div className="vc-kpiHint">last 30 days</div>
              </div>
              <div className="vc-kpi">
                <div className="vc-kpiLabel">🗓️ Active days</div>
                <div className="vc-kpiValue">
                  {isActivityLoading ? '…' : activityStats.activeDaysThisMonth}
                </div>
                <div className="vc-kpiHint">this month</div>
              </div>
            </div>
          </section>
        </div>

        {/* Distance summary */}
        <section className="vc-card vc-cardPad" style={{ marginTop: 16 }}>
          <div className="vc-cardTitle">
            <span>Distance</span>
            <span className="vc-muted" style={{ fontWeight: 700, fontSize: 12 }}>
              Active {totalActiveChallenges} · Inactive {totalCompletedChallenges}
            </span>
          </div>
          <div className="vc-kpiRow">
            <div className="vc-kpi">
              <div className="vc-kpiLabel">So far</div>
              <div className="vc-kpiValue">
                {isKilometers ? totalSoFar.toFixed(0) : (totalSoFar * 0.621371).toFixed(1)}
              </div>
              <div className="vc-kpiHint">{isKilometers ? 'km' : 'miles'}</div>
            </div>
            <div className="vc-kpi">
              <div className="vc-kpiLabel">Target</div>
              <div className="vc-kpiValue">
                {isKilometers ? totalTarget.toFixed(0) : (totalTarget * 0.621371).toFixed(1)}
              </div>
              <div className="vc-kpiHint">{isKilometers ? 'km' : 'miles'}</div>
            </div>
          </div>
        </section>

      {/* ===== TABS ===== */}
 <div className="segmented-tabs">
  <button
    className={`segment ${activeTab === 1 ? "active" : ""}`}
    onClick={() => setActiveTab(1)}
    type="button"
  >
    Active
  </button>

  <button
    className={`segment ${activeTab === 2 ? "active" : ""}`}
    onClick={() => setActiveTab(2)}
    type="button"
  >
    Inactive
  </button>
</div>



      {/* ===== FILTER & SEARCH BAR ===== */}
      <section className="filter-search-bar">
        <div className="filters">
          <button
            className={filterPercentageSort ? 'btn active' : 'btn'}
            onClick={() => setFilterPercentageSort((prev) => !prev)}
            title="Sort by % (desc)"
          >
            <img src={IconTrainer} alt="trainer" />
          </button>
          <button
            className={filterTimeSort ? 'btn active' : 'btn'}
            onClick={() => setFilterTimeSort((prev) => !prev)}
            title="Sort by Days Left (asc)"
            disabled={activeTab === 2}
          >
            <img src={IconCalendar} alt="calendar" />
          </button>
          <button
            className={filterZeroCompletion ? 'btn active' : 'btn'}
            onClick={() => setFilterZeroCompletion((prev) => !prev)}
            title="% = 0"
          >
            <img src={IconStart} alt="start" />
          </button>
          <button
            className={filterDistanceGT0 ? 'btn active' : 'btn'}
            onClick={() => setFilterDistanceGT0((prev) => !prev)}
            title="Distance > 0"
          >
            <img src={IconDistance} alt="distance" />
          </button>
          <button
            className="btn toggle-unit"
            onClick={() => setIsKilometers((prev) => !prev)}
            title="Toggle KM / Mi"
          >
            {isKilometers ? 'Mi' : 'KM'}
          </button>
        </div>
      </section>

      {/* ===== GRID OF CHALLENGE CARDS ===== */}
      <section className="challenges-grid-container">
        {isLoading ? (
          <div className="loading-wrapper">
            <div className="loader"></div>
            <p>Loading challenges…</p>
          </div>
        ) : filtered.length === 0 ? (
          <p className="no-data">No Challenges Found</p>
        ) : (
          <div className="challenges-grid">
            {filtered.map((ch) => {
              const apiDaysLeft =
                typeof ch.daysLeftToComplete === 'number'
                  ? ch.daysLeftToComplete
                  : null;

              let expiryLabel = "Expiry";
              let expiryText: React.ReactNode = null;

              if (apiDaysLeft !== null) {
                const now = new Date();
                const expiryDate = new Date(now);
                expiryDate.setDate(now.getDate() + apiDaysLeft);
                const dateStr = expiryDate.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                });

                if (ch.percentage >= 100) {
                  expiryText = 'Completed';
                } else if (apiDaysLeft < 0 || apiDaysLeft === 0) {
                  expiryText = 'Expired';
                } else if (apiDaysLeft === 1) {
                  expiryText = (
                    <>
                      {dateStr}
                      <br />
                      <span style={{ color: '#888', fontSize: '0.95em' }}>Tomorrow</span>
                    </>
                  );
                } else if (apiDaysLeft > 1) {
                  expiryText = (
                    <>
                      {dateStr}
                      <br />
                      <span style={{ color: '#888', fontSize: '0.95em' }}>
                        in {apiDaysLeft} days
                      </span>
                    </>
                  );
                }
              } else {
                expiryText = 'Not Yet Started';
              }

              return (
                <div
                  key={ch.id}
                  className={`challenge-card ${getCardAccentClass(ch.percentage)}`}
                  onClick={() =>
                    navigate(`/map/${brandId}/${ch.productId}/${userId}`)
                  }
                >
                  <div className="card-image">
                    <img src={getMapImage(ch.images)} alt={ch.name} />
                  </div>

                  <div className="card-content-grid">
                    <div className="grid-title">{ch.name}</div>

                    <div className="grid-label">So Far</div>
                    <div className="grid-value bold">
                      {isKilometers
                        ? ch.distanceKm.toFixed(1)
                        : (ch.distanceKm * 0.621371).toFixed(1)}{' '}
                      {isKilometers ? 'KM' : 'Mi'}
                    </div>

                    <div className="grid-label">Target</div>
                    <div className="grid-value bold">
                      {isKilometers
                        ? ch.totalDistanceKm.toFixed(0)
                        : (ch.totalDistanceKm * 0.621371).toFixed(1)}{' '}
                      {isKilometers ? 'KM' : 'Mi'}
                    </div>

                    <div className="grid-label">%</div>
                    <div className="grid-value bold">
                      {ch.percentage.toFixed(1)}%
                    </div>

                    <div className="grid-label">{expiryLabel}</div>
                    <div className="grid-value">{expiryText}</div>
                  </div>

                  <div className="progress-bar-wrapper thicker">
                    <div className="progress-bar-bg">
                      <div
                        className="progress-bar-fg"
                        style={{ width: `${Math.min(ch.percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      <section className="badges-row-section">
        <BadgesRow
          completedChallenges={totalCompletedChallenges}
          totalDistanceKm={totalSoFar}
          isKilometers={isKilometers}
        />
      </section>
      </div>
    </div>
  );
}

// Helper for accent colors
function getCardAccentClass(pct: number) {
  if (pct <= 1) return 'accent-white';
  if (pct <= 20) return 'accent-light-bronze';
  if (pct <= 40) return 'accent-bronze';
  if (pct <= 60) return 'accent-light-silver';
  if (pct <= 75) return 'accent-silver';
  if (pct <= 90) return 'accent-light-gold';
  return 'accent-gold';
}
